import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Filter, ChevronDown, Download, Printer, TrendingUp, Wallet, BarChart3, Moon, Receipt, Percent, Building2, Clock, XCircle, PieChart as PieIcon, Save } from "lucide-react";
import api, { formatTRY } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminLayout from "@/components/AdminLayout";
import HotelsComparisonTab from "@/pages/admin/reports/HotelsComparisonTab";
import AgingTab from "@/pages/admin/reports/AgingTab";
import CancellationsTab from "@/pages/admin/reports/CancellationsTab";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const STATUSES = [
    { value: "pending", label: "Beklemede" },
    { value: "confirmed", label: "Onaylandı" },
    { value: "cancelled", label: "İptal" },
    { value: "completed", label: "Tamamlandı" },
];
const PAYMENT_STATUSES = [
    { value: "", label: "Tüm Ödemeler" },
    { value: "initiated", label: "Başlatıldı" },
    { value: "paid", label: "Ödendi" },
    { value: "partial", label: "Kısmi" },
    { value: "failed", label: "Başarısız" },
];
const SOURCES = [
    { value: "", label: "Tüm Kaynaklar" },
    { value: "direct", label: "Direkt" },
    { value: "agency", label: "Acente" },
    { value: "phone", label: "Telefon" },
];
const DATE_FIELDS = [
    { value: "created_at", label: "Oluşturma" },
    { value: "check_in", label: "Giriş" },
    { value: "check_out", label: "Çıkış" },
];
const GRANS = [
    { value: "day", label: "Günlük" },
    { value: "week", label: "Haftalık" },
    { value: "month", label: "Aylık" },
];

const ymd = (d) => d.toISOString().slice(0, 10);
const PRESETS = [
    { key: "30d", label: "Son 30 gün", apply: (d) => { const s = new Date(d); s.setDate(d.getDate() - 30); return { date_field: "created_at", date_from: ymd(s), date_to: ymd(d) }; } },
    { key: "90d", label: "Son 90 gün", apply: (d) => { const s = new Date(d); s.setDate(d.getDate() - 90); return { date_field: "created_at", date_from: ymd(s), date_to: ymd(d) }; } },
    { key: "month", label: "Bu ay", apply: (d) => { const s = new Date(d.getFullYear(), d.getMonth(), 1); const e = new Date(d.getFullYear(), d.getMonth() + 1, 0); return { date_field: "created_at", date_from: ymd(s), date_to: ymd(e) }; } },
    { key: "year", label: "Bu yıl", apply: (d) => { const s = new Date(d.getFullYear(), 0, 1); const e = new Date(d.getFullYear(), 11, 31); return { date_field: "created_at", date_from: ymd(s), date_to: ymd(e) }; } },
    { key: "checkin_week", label: "Hafta giriş", apply: (d) => { const dow = d.getDay() || 7; const m = new Date(d); m.setDate(d.getDate() - dow + 1); const s = new Date(m); s.setDate(m.getDate() + 6); return { date_field: "check_in", date_from: ymd(m), date_to: ymd(s) }; } },
];

const FLT_INPUT = "w-full px-2 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-medium text-slate-900 dark:text-white";

export default function AdminReportsPage() {
    const { user, loading: authLoading } = useAuth() || {};
    const navigate = useNavigate();
    const today = ymd(new Date());
    const monthAgo = ymd(new Date(Date.now() - 30 * 86400000));

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showFilters, setShowFilters] = useState(false);
    const [hotelsList, setHotelsList] = useState([]);

    // Filters
    const [q, setQ] = useState("");
    const [dateField, setDateField] = useState("created_at");
    const [dateFrom, setDateFrom] = useState(monthAgo);
    const [dateTo, setDateTo] = useState(today);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [paymentStatus, setPaymentStatus] = useState("");
    const [source, setSource] = useState("");
    const [selectedHotelIds, setSelectedHotelIds] = useState([]);
    const [minTotal, setMinTotal] = useState("");
    const [maxTotal, setMaxTotal] = useState("");
    const [granularity, setGranularity] = useState("day");
    // Sekme sistemi
    const [tab, setTab] = useState("overview"); // overview | hotels | aging | cancellations
    // Kayıtlı filtreler (localStorage)
    const [savedFilters, setSavedFilters] = useState(() => {
        try { return JSON.parse(localStorage.getItem("ms_saved_report_filters") || "[]"); } catch { return []; }
    });

    const buildParams = () => {
        const p = new URLSearchParams();
        if (q) p.set("q", q);
        if (dateField) p.set("date_field", dateField);
        if (dateFrom) p.set("date_from", dateFrom);
        if (dateTo) p.set("date_to", dateTo);
        if (selectedStatuses.length) p.set("statuses", selectedStatuses.join(","));
        if (paymentStatus) p.set("payment_status", paymentStatus);
        if (source) p.set("source", source);
        if (selectedHotelIds.length) p.set("hotel_ids", selectedHotelIds.join(","));
        if (minTotal) p.set("min_total", minTotal);
        if (maxTotal) p.set("max_total", maxTotal);
        p.set("granularity", granularity);
        return p;
    };

    const load = () => {
        setLoading(true);
        api.get(`/admin/reports/overview?${buildParams().toString()}`)
            .then((r) => setData(r.data))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (authLoading) return;
        if (!user || !["super_admin", "admin"].includes(user.role)) return navigate("/login");
        load();
        api.get("/admin/hotels?limit=200").then((r) => setHotelsList(r.data.items || r.data || [])).catch(() => {});
    }, [user, authLoading]); // eslint-disable-line

    useEffect(() => {
        const t = setTimeout(load, 300);
        return () => clearTimeout(t);
    }, [q, dateField, dateFrom, dateTo, selectedStatuses, paymentStatus, source, selectedHotelIds, minTotal, maxTotal, granularity]); // eslint-disable-line

    const applyPreset = (key) => {
        const p = PRESETS.find((x) => x.key === key);
        if (!p) return;
        const v = p.apply(new Date());
        setDateField(v.date_field); setDateFrom(v.date_from); setDateTo(v.date_to);
    };

    const clearFilters = () => {
        setQ(""); setSelectedStatuses([]); setPaymentStatus(""); setSource(""); setSelectedHotelIds([]);
        setMinTotal(""); setMaxTotal(""); setDateField("created_at"); setDateFrom(monthAgo); setDateTo(today);
    };

    const exportXlsx = () => {
        const token = localStorage.getItem("ms_token");
        fetch(`${api.defaults.baseURL}/admin/reports/export.xlsx?${buildParams().toString()}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((b) => {
                const a = document.createElement("a");
                const u = URL.createObjectURL(b);
                a.href = u;
                a.download = `rapor_${new Date().toISOString().slice(0, 10)}.xlsx`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(u);
            });
    };

    // Kayıtlı filtreleri sakla
    const persistSaved = (list) => {
        setSavedFilters(list);
        localStorage.setItem("ms_saved_report_filters", JSON.stringify(list));
    };
    const saveCurrentFilter = () => {
        const name = window.prompt("Bu filtreye isim verin:", "");
        if (!name) return;
        const filter = { name, q, dateField, dateFrom, dateTo, selectedStatuses, paymentStatus, source, selectedHotelIds, minTotal, maxTotal, granularity };
        persistSaved([...savedFilters.filter((f) => f.name !== name), filter]);
    };
    const loadSavedFilter = (f) => {
        setQ(f.q || ""); setDateField(f.dateField || "created_at");
        setDateFrom(f.dateFrom || monthAgo); setDateTo(f.dateTo || today);
        setSelectedStatuses(f.selectedStatuses || []); setPaymentStatus(f.paymentStatus || "");
        setSource(f.source || ""); setSelectedHotelIds(f.selectedHotelIds || []);
        setMinTotal(f.minTotal || ""); setMaxTotal(f.maxTotal || "");
        setGranularity(f.granularity || "day");
    };
    const deleteSavedFilter = (name) => persistSaved(savedFilters.filter((f) => f.name !== name));

    const k = data?.kpis || { bookings: 0, nights: 0, sales: 0, cost: 0, profit: 0, paid: 0, remaining: 0, avg_basket: 0, adr: 0, margin_pct: 0 };
    const series = data?.series || [];
    const maxSales = series.length ? Math.max(...series.map((s) => s.sales), 1) : 1;

    return (
        <AdminLayout title="Raporlar">
            {/* Sekme Barı */}
            <div className="mb-4 flex flex-wrap gap-2" data-testid="reports-tabs">
                <TabBtn active={tab === "overview"} onClick={() => setTab("overview")} icon={PieIcon} label="Genel Rapor" testid="tab-overview" />
                <TabBtn active={tab === "hotels"} onClick={() => setTab("hotels")} icon={Building2} label="Otel Karşılaştırma" testid="tab-hotels" />
                <TabBtn active={tab === "aging"} onClick={() => setTab("aging")} icon={Clock} label="Yaşlandırma (Alacak)" testid="tab-aging-btn" />
                <TabBtn active={tab === "cancellations"} onClick={() => setTab("cancellations")} icon={XCircle} label="İptal & İade" testid="tab-cancellations-btn" />
            </div>

            {tab === "overview" && (
                <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-2 mb-4">
                <KpiCard icon={<Receipt className="h-3.5 w-3.5" />} label="Rezervasyon" value={k.bookings} color="slate" />
                <KpiCard icon={<Moon className="h-3.5 w-3.5" />} label="Geceleme" value={k.nights} color="indigo" />
                <KpiCard icon={<TrendingUp className="h-3.5 w-3.5" />} label="Satış" value={formatTRY(k.sales)} color="sky" />
                <KpiCard icon={<BarChart3 className="h-3.5 w-3.5" />} label="Maliyet" value={formatTRY(k.cost)} color="amber" />
                <KpiCard icon={<TrendingUp className="h-3.5 w-3.5" />} label="Kar" value={formatTRY(k.profit)} color="emerald" />
                <KpiCard icon={<Percent className="h-3.5 w-3.5" />} label="Kar Marjı" value={`${k.margin_pct}%`} color="emerald" />
                <KpiCard icon={<Wallet className="h-3.5 w-3.5" />} label="Tahsil" value={formatTRY(k.paid)} color="emerald" />
                <KpiCard icon={<Wallet className="h-3.5 w-3.5" />} label="Kalan" value={formatTRY(k.remaining)} color="rose" />
                <KpiCard icon={<TrendingUp className="h-3.5 w-3.5" />} label="Ort. Sepet" value={formatTRY(k.avg_basket)} color="sky" />
                <KpiCard icon={<Moon className="h-3.5 w-3.5" />} label="ADR" value={formatTRY(k.adr)} color="indigo" />
                <KpiCard icon={<Moon className="h-3.5 w-3.5" />} label="Ort. Konaklama" value={`${k.avg_los || 0} gece`} color="violet" />
                <KpiCard icon={<Clock className="h-3.5 w-3.5" />} label="Rez.→Giriş" value={`${k.avg_booking_window || 0} gün`} color="violet" />
                <KpiCard icon={<XCircle className="h-3.5 w-3.5" />} label="İptal Oranı" value={`%${k.cancel_rate_pct || 0}`} color="rose" />
                <KpiCard icon={<XCircle className="h-3.5 w-3.5" />} label="İade Oranı" value={`%${k.refund_rate_pct || 0}`} color="orange" />
            </div>

            {/* Kayıtlı filtreler + Save */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
                {savedFilters.length > 0 && <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Kayıtlı Filtreler:</span>}
                {savedFilters.map((f) => (
                    <div key={f.name} className="inline-flex items-center gap-1 bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 px-2 py-1 rounded-lg text-[11px]">
                        <button onClick={() => loadSavedFilter(f)} className="font-semibold" data-testid={`load-filter-${f.name}`}>{f.name}</button>
                        <button onClick={() => deleteSavedFilter(f.name)} className="opacity-60 hover:opacity-100"><X className="h-3 w-3" /></button>
                    </div>
                ))}
                <button onClick={saveCurrentFilter} data-testid="save-filter-btn" className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-[11px] font-semibold">
                    <Save className="h-3 w-3" /> Filtreyi Kaydet
                </button>
            </div>

            {/* Filter bar */}
            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 mb-4 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-[220px] px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                        <Search className="h-4 w-4 text-slate-400" />
                        <input data-testid="report-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ad, otel, voucher..." className="w-full bg-transparent outline-none text-sm" />
                        {q && <button onClick={() => setQ("")}><X className="h-3.5 w-3.5 text-slate-400" /></button>}
                    </div>
                    <button onClick={() => setShowFilters((s) => !s)} data-testid="toggle-filters-btn" className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border transition-colors ${showFilters ? "bg-sky-500 border-sky-500 text-white" : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700"}`}>
                        <Filter className="h-3.5 w-3.5" /> Filtre
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showFilters ? "rotate-180" : ""}`} />
                    </button>
                    <div className="flex flex-wrap gap-1">
                        {PRESETS.map((p) => (
                            <button key={p.key} onClick={() => applyPreset(p.key)} data-testid={`preset-${p.key}`} className="px-3 py-2 rounded-xl text-xs font-semibold bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700">
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <div className="inline-flex rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 ml-auto">
                        {GRANS.map((g) => (
                            <button key={g.value} onClick={() => setGranularity(g.value)} data-testid={`gran-${g.value}`} className={`px-3 py-2 text-xs font-bold transition-colors ${granularity === g.value ? "bg-sky-500 text-white" : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                                {g.label}
                            </button>
                        ))}
                    </div>
                    <button onClick={exportXlsx} data-testid="export-xlsx-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-emerald-500 hover:bg-emerald-600 text-white">
                        <Download className="h-3.5 w-3.5" /> Excel
                    </button>
                    <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
                        <Printer className="h-3.5 w-3.5" /> Yazdır
                    </button>
                </div>

                {showFilters && (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                        <Field label="Tarih Alanı">
                            <select value={dateField} onChange={(e) => setDateField(e.target.value)} className={FLT_INPUT}>
                                {DATE_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                            </select>
                        </Field>
                        <Field label="Başlangıç">
                            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={FLT_INPUT} data-testid="filter-date-from" />
                        </Field>
                        <Field label="Bitiş">
                            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={FLT_INPUT} data-testid="filter-date-to" />
                        </Field>
                        <Field label="Ödeme Durumu">
                            <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)} className={FLT_INPUT}>
                                {PAYMENT_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                        </Field>
                        <Field label="Kaynak">
                            <select value={source} onChange={(e) => setSource(e.target.value)} className={FLT_INPUT}>
                                {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                        </Field>
                        <Field label="Tutar (₺)">
                            <div className="flex gap-1">
                                <input type="number" placeholder="Min" value={minTotal} onChange={(e) => setMinTotal(e.target.value)} className={FLT_INPUT} />
                                <input type="number" placeholder="Max" value={maxTotal} onChange={(e) => setMaxTotal(e.target.value)} className={FLT_INPUT} />
                            </div>
                        </Field>
                        <div className="col-span-2 md:col-span-4 lg:col-span-3">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Durum</div>
                            <div className="flex flex-wrap gap-1">
                                {STATUSES.map((s) => (
                                    <button key={s.value} onClick={() => setSelectedStatuses((arr) => arr.includes(s.value) ? arr.filter((x) => x !== s.value) : [...arr, s.value])} className={`px-3 py-1 rounded-full text-[11px] font-semibold ${selectedStatuses.includes(s.value) ? "bg-sky-500 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300"}`}>{s.label}</button>
                                ))}
                            </div>
                        </div>
                        <div className="col-span-2 md:col-span-4 lg:col-span-3">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Otel</div>
                            <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                                {hotelsList.map((h) => (
                                    <button key={h.id} onClick={() => setSelectedHotelIds((arr) => arr.includes(h.id) ? arr.filter((x) => x !== h.id) : [...arr, h.id])} className={`px-3 py-1 rounded-full text-[11px] font-semibold ${selectedHotelIds.includes(h.id) ? "bg-sky-500 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300"}`}>{h.name}</button>
                                ))}
                            </div>
                        </div>
                        <div className="md:col-span-4 lg:col-span-6 flex justify-end">
                            <button onClick={clearFilters} className="text-xs font-semibold text-slate-500 hover:text-slate-700">Filtreleri temizle</button>
                        </div>
                    </div>
                )}
            </div>

            {loading ? (
                <div className="p-8 text-center text-slate-500 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">Yükleniyor...</div>
            ) : (
                <div className="space-y-4">
                    {/* Series chart */}
                    <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                        <h2 className="font-display text-base font-bold mb-3 text-slate-900 dark:text-white">Zaman Serisi ({GRANS.find((g) => g.value === granularity)?.label})</h2>
                        {series.length === 0 ? (
                            <p className="text-slate-500 text-xs">Bu aralıkta veri yok.</p>
                        ) : (
                            <div className="space-y-1.5">
                                {series.map((s) => (
                                    <div key={s.label} className="flex items-center gap-3 text-xs">
                                        <div className="w-24 font-mono text-slate-600 dark:text-slate-300">{s.label}</div>
                                        <div className="flex-1 h-7 bg-slate-100 dark:bg-slate-800 rounded-md overflow-hidden relative">
                                            <div className="h-full bg-gradient-to-r from-sky-400 to-emerald-500" style={{ width: `${(s.sales / maxSales) * 100}%` }} />
                                            <div className="absolute inset-0 flex items-center justify-end pr-2 text-[10px] font-bold text-white drop-shadow-sm">{formatTRY(s.sales)}</div>
                                        </div>
                                        <div className="w-16 text-right text-slate-500 text-[11px]">{s.bookings} rez</div>
                                        <div className="w-16 text-right text-slate-500 text-[11px]">{s.nights}n</div>
                                        <div className="w-24 text-right text-emerald-600 font-semibold text-[11px]">{formatTRY(s.profit)}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Breakdown grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <BreakdownPanel title="Destinasyona Göre" items={data.by_destination} testid="bd-destination" />
                        <BreakdownPanel title="Otele Göre" items={data.by_hotel} testid="bd-hotel" />
                        <BreakdownPanel title="Oda Tipine Göre" items={data.by_room_type} testid="bd-room" />
                        <BreakdownPanel title="Fiyat Planına Göre" items={data.by_plan} testid="bd-plan" />
                        <BreakdownPanel title="Duruma Göre" items={data.by_status} compact testid="bd-status" />
                        <BreakdownPanel title="Kaynağa Göre" items={data.by_source} compact testid="bd-source" />
                    </div>

                    {/* ====== Filter Totals — bottom summary (Excel-style totals row) ====== */}
                    <div data-testid="report-totals-card" className="p-5 rounded-2xl bg-gradient-to-br from-sky-50 via-emerald-50 to-amber-50 dark:from-sky-950/40 dark:via-emerald-950/30 dark:to-amber-950/30 border-2 border-emerald-300 dark:border-emerald-800/60 shadow-sm">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="h-7 w-7 rounded-lg bg-emerald-500 text-white flex items-center justify-center font-bold">Σ</div>
                            <div>
                                <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">Filtre Toplamları</h2>
                                <p className="text-[11px] text-slate-600 dark:text-slate-300">{data.from} → {data.to} aralığı · {selectedHotelIds.length > 0 ? `${selectedHotelIds.length} otel` : "tüm oteller"}</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                            <TotalCell label="Rezervasyon" value={k.bookings} testid="total-bookings" />
                            <TotalCell label="Geceleme" value={k.nights} testid="total-nights" />
                            <TotalCell label="Toplam Satış" value={formatTRY(k.sales)} color="sky" testid="total-sales" />
                            <TotalCell label="Toplam Maliyet" value={formatTRY(k.cost)} color="amber" testid="total-cost" />
                            <TotalCell label="Toplam Kar" value={formatTRY(k.profit)} color="emerald" sub={`Marj ${k.margin_pct}%`} testid="total-profit" />
                            <TotalCell label="Alınan" value={formatTRY(k.paid)} color="emerald" testid="total-paid" />
                            <TotalCell label="Kalan" value={formatTRY(k.remaining)} color="rose" testid="total-remaining" />
                            <TotalCell label="Ort. Sepet" value={formatTRY(k.avg_basket)} color="indigo" testid="total-avg-basket" />
                            <TotalCell label="ADR / Gece" value={formatTRY(k.adr)} color="indigo" testid="total-adr" />
                        </div>
                    </div>
                </div>
            )}
            </>
            )}

            {/* Diğer sekmeler */}
            {tab === "hotels" && <HotelsComparisonTab dateFrom={dateFrom} dateTo={dateTo} />}
            {tab === "aging" && <AgingTab />}
            {tab === "cancellations" && <CancellationsTab dateFrom={dateFrom} dateTo={dateTo} />}
        </AdminLayout>
    );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold transition-all ${active ? "bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white shadow-lg shadow-violet-500/25" : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800"}`}
        >
            <Icon className="h-4 w-4" />
            {label}
        </button>
    );
}

function TotalCell({ label, value, color = "slate", sub, testid }) {
    const palette = {
        slate: "text-slate-900 dark:text-white",
        sky: "text-sky-700 dark:text-sky-300",
        emerald: "text-emerald-700 dark:text-emerald-300",
        amber: "text-amber-700 dark:text-amber-300",
        rose: "text-rose-700 dark:text-rose-300",
        indigo: "text-indigo-700 dark:text-indigo-300",
    }[color] || "";
    return (
        <div data-testid={testid} className="bg-white/70 dark:bg-slate-900/60 rounded-xl p-3 border border-white/60 dark:border-slate-800">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
            <div className={`text-lg font-display font-bold ${palette}`}>{value}</div>
            {sub && <div className="text-[10px] mt-0.5 text-slate-500">{sub}</div>}
        </div>
    );
}

function KpiCard({ icon, label, value, color = "slate" }) {
    const palette = {
        slate: "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-200",
        sky: "bg-sky-50 dark:bg-sky-950/30 border-sky-200 dark:border-sky-900 text-sky-700 dark:text-sky-300",
        emerald: "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900 text-emerald-700 dark:text-emerald-300",
        amber: "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900 text-amber-700 dark:text-amber-300",
        rose: "bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-300",
        indigo: "bg-indigo-50 dark:bg-indigo-950/30 border-indigo-200 dark:border-indigo-900 text-indigo-700 dark:text-indigo-300",
    }[color] || "";
    return (
        <div className={`rounded-xl border p-3 ${palette}`}>
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest opacity-80">{icon} {label}</div>
            <div className="mt-1 text-base font-bold">{value}</div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
            {children}
        </div>
    );
}

function BreakdownPanel({ title, items, testid, compact }) {
    if (!items) items = [];
    const max = items.length ? Math.max(...items.map((i) => i.sales), 1) : 1;
    const totals = items.reduce((acc, it) => ({
        sales: acc.sales + (it.sales || 0),
        cost: acc.cost + (it.cost || 0),
        profit: acc.profit + (it.profit || 0),
        bookings: acc.bookings + (it.bookings || 0),
        nights: acc.nights + (it.nights || 0),
    }), { sales: 0, cost: 0, profit: 0, bookings: 0, nights: 0 });
    return (
        <div data-testid={testid} className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
            <h3 className="font-display text-base font-bold mb-3 text-slate-900 dark:text-white">{title}</h3>
            {items.length === 0 ? (
                <p className="text-slate-500 text-xs">Veri yok.</p>
            ) : (
                <>
                    <div className="space-y-2">
                        {items.slice(0, compact ? 6 : 10).map((it, i) => (
                            <div key={i} className="space-y-1">
                                <div className="flex justify-between items-center gap-2 text-xs">
                                    <span className="font-semibold text-slate-900 dark:text-white truncate max-w-[60%]">{it.label}</span>
                                    <div className="flex items-center gap-3 text-[11px]">
                                        <span className="text-slate-500">{it.bookings} rez · {it.nights}n</span>
                                        <span className="font-bold text-sky-600 dark:text-sky-400">{formatTRY(it.sales)}</span>
                                        <span className="font-bold text-emerald-600 dark:text-emerald-400 w-20 text-right">{formatTRY(it.profit)}</span>
                                    </div>
                                </div>
                                <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded">
                                    <div className="h-full bg-gradient-to-r from-sky-500 to-emerald-500 rounded" style={{ width: `${(it.sales / max) * 100}%` }} />
                                </div>
                            </div>
                        ))}
                    </div>
                    {/* Totals row */}
                    <div data-testid={`${testid}-totals`} className="mt-3 pt-3 border-t-2 border-dashed border-slate-200 dark:border-slate-700 flex justify-between items-center gap-2 text-xs">
                        <span className="font-bold uppercase tracking-widest text-slate-700 dark:text-slate-200">Toplam</span>
                        <div className="flex items-center gap-3 text-[11px]">
                            <span className="text-slate-500">{totals.bookings} rez · {totals.nights}n</span>
                            <span className="font-bold text-sky-700 dark:text-sky-300">{formatTRY(totals.sales)}</span>
                            <span className="font-bold text-emerald-700 dark:text-emerald-300 w-20 text-right">{formatTRY(totals.profit)}</span>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
